import { useState } from 'react';
import { AdminUser, Comment } from '../../pm/types';
import { displayName, MAX_COMMENT_DEPTH } from '../../pm/constants';
import { timeAgo } from '../../pm/timeAgo';
import { createComment, deleteCommentTree } from '../../pm/firebase';
import { linkify } from '../../pm/linkify';
import CommentComposer from './CommentComposer';

interface Props {
  comment: Comment;
  allComments: Comment[];
  childrenByParent: Record<string, Comment[]>;
  admins: AdminUser[];
  currentUid: string;
  projectId: string;
  depth: number;
}

function renderBody(body: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /@[A-Za-z0-9_]+/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(body)) !== null) {
    if (match.index > last) {
      parts.push(...linkify(body.slice(last, match.index), { keyPrefix: `u-${last}` }));
    }
    parts.push(
      <span key={`m-${match.index}`} className="pm-mention">{match[0]}</span>,
    );
    last = match.index + match[0].length;
  }
  if (last < body.length) {
    parts.push(...linkify(body.slice(last), { keyPrefix: `u-${last}` }));
  }
  return parts;
}

const CommentNode: React.FC<Props> = ({
  comment,
  allComments,
  childrenByParent,
  admins,
  currentUid,
  projectId,
  depth,
}) => {
  const [replying, setReplying] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const authorEmail = admins.find((a) => a.uid === comment.authorId)?.email;
  const authorName = displayName(authorEmail) || 'Unknown';
  const children = childrenByParent[comment.id] || [];
  const canNestDeeper = depth < MAX_COMMENT_DEPTH;

  function countDescendants(id: string): number {
    const direct = childrenByParent[id] || [];
    return direct.reduce((acc, c) => acc + 1 + countDescendants(c.id), 0);
  }
  const descendantCount = countDescendants(comment.id);

  async function handleReply(body: string, mentions: string[]) {
    await createComment(
      { taskId: comment.taskId, projectId, parentId: comment.id, body, mentions },
      currentUid,
    );
    setReplying(false);
  }

  async function handleDelete() {
    const msg = descendantCount > 0
      ? `Delete this comment and ${descendantCount} ${descendantCount === 1 ? 'reply' : 'replies'} below it?`
      : 'Delete this comment?';
    if (!window.confirm(msg)) return;
    await deleteCommentTree(comment.id, allComments);
  }

  if (collapsed) {
    return (
      <div className="pm-comment-node">
        <div className="pm-comment pm-comment-collapsed">
          <button
            className="pm-collapse-toggle"
            onClick={() => setCollapsed(false)}
            aria-label="Expand comment"
          >
            [+]
          </button>
          <strong className="pm-comment-author">{authorName}</strong>
          <span className="pm-comment-dot">·</span>
          <span>{timeAgo(comment.createdAt)}</span>
          {descendantCount > 0 ? (
            <>
              <span className="pm-comment-dot">·</span>
              <span>
                {descendantCount} {descendantCount === 1 ? 'reply' : 'replies'} hidden
              </span>
            </>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="pm-comment-node">
      <div className="pm-comment">
        <div className="pm-comment-meta">
          <button
            className="pm-collapse-toggle"
            onClick={() => setCollapsed(true)}
            aria-label="Collapse comment"
          >
            [-]
          </button>
          <strong className="pm-comment-author">{authorName}</strong>
          <span className="pm-comment-dot">·</span>
          <span>{timeAgo(comment.createdAt)}</span>
        </div>
        <div className="pm-comment-body">{renderBody(comment.body)}</div>
        <div className="pm-comment-actions">
          {canNestDeeper ? (
            <button className="pm-link-button" onClick={() => setReplying((v) => !v)}>
              {replying ? 'Cancel reply' : 'Reply'}
            </button>
          ) : null}
          <button className="pm-link-button pm-danger" onClick={handleDelete}>
            Delete
          </button>
        </div>
        {replying ? (
          <div className="pm-comment-reply-form">
            <CommentComposer
              admins={admins}
              placeholder={`Reply to ${authorName}…`}
              submitLabel="Reply"
              autoFocus
              onSubmit={handleReply}
              onCancel={() => setReplying(false)}
            />
          </div>
        ) : null}
      </div>
      {children.length > 0 ? (
        <div className="pm-comment-children">
          {children.map((c) => (
            <CommentNode
              key={c.id}
              comment={c}
              allComments={allComments}
              childrenByParent={childrenByParent}
              admins={admins}
              currentUid={currentUid}
              projectId={projectId}
              depth={depth + 1}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default CommentNode;
